// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";

// mui
import { Stack, Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography, FormControl, Select, Divider } from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartNumberInput } from "../../../components/SmartInputs";

// theme
import {
  B1Tiny,
  ModalActionsLoadingButton,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
  ModalAlertError,
  ModalContentSwitch,
} from "../../../theme/styled";

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const InitialTakeProfitDefaults = {
  nSelection: "1",
  dRiskRatio: "1.40",
  nFixPipsActual: "300",
  dRateOfChangeActual: "0.00060000",
  nInosUsedForValueInjection: "0",
  nCaSupResUsedForValueInjection: "0",
  bFixPipsMin: false,
  nFixPipsMin: "4",
  bRateOfChangeMin: false,
  dRateOfChangeMin: "0.00000100",
  bFixPipsMax: false,
  nFixPipsMax: "400",
  bRateOfChangeMax: false,
  dRateOfChangeMax: "0.00001000",
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  dRiskRatio: {
    digits: 2,
    min: 0.10,
    step: 0.05,
  },
  nFixPipsActual: {
    min: 1,
  },
  dRateOfChangeActual: {
    digits: 8,
    step: 0.00001,
    min: 0.00000001,
  },
  nFixPipsMin: {
    min: 1,
  },
  dRateOfChangeMin: {
    digits: 8,
    step: 0.00001,
    min: 0.00000001,
  },
  nFixPipsMax: {
    min: 1,
  },
  dRateOfChangeMax: {
    digits: 8,
    step: 0.00001,
    min: 0.00000001,
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function InitialTakeProfitRenderParameters(professionalMode, config, indicators, chartAnalyzers) {
  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get display values
  const getDisplayValue = (key) => config[key];

  // Helper function to get indicator name by ID
  const getIndicatorNameById = (indicatorId) => {
    if (!indicatorId || indicatorId === "0") return "None selected";
    if (!indicators || indicators.length === 0) return "No indicators available";

    const indicator = indicators.find((ind) => ind.id.toString() === indicatorId.toString());
    return indicator ? `${indicator.name} (${indicator.subtitle})` : `Unknown indicator (ID: ${indicatorId})`;
  };

  // Helper function to get chart analyzer name by ID
  const getChartAnalyzerNameById = (chartAnalyzerId) => {
    if (!chartAnalyzerId || chartAnalyzerId === "0") return "None selected";
    if (!chartAnalyzers || chartAnalyzers.length === 0) return "No chart analyzers available";

    const chartAnalyzer = chartAnalyzers.find((ca) => ca.id.toString() === chartAnalyzerId.toString());
    return chartAnalyzer ? `${chartAnalyzer.name} (${chartAnalyzer.subtitle})` : `Unknown chart analyzer (ID: ${chartAnalyzerId})`;
  };

  const getSelectionDisplay = () => {
    const value = config.nSelection;
    switch (value) {
      case "0":
        return "Disabled";
      case "1":
        return "Risk ratio";
      case "2":
        return "Fix pips";
      case "3":
        return "Rate of change";
      case "4":
        return "Indicator value";
      case "5":
        return "Support/resistance from chart analyzer";
      case "6":
        return "Candle patterns";
      case "7":
        return "Chart patterns";
      case "8":
        return "Fibonacci patterns";
      default:
        return "Not set";
    }
  };

  return (
    <Stack spacing={0.5}>
      {renderParam("Selection", getSelectionDisplay())}

      {config.nSelection === "1" ? <>{renderParam("Risk ratio", getDisplayValue("dRiskRatio"))}</> : null}

      {config.nSelection === "2" ? <>{renderParam("Fix pips", getDisplayValue("nFixPipsActual"))}</> : null}

      {config.nSelection === "3" ? <>{renderParam("Rate change", getDisplayValue("dRateOfChangeActual"))}</> : null}

      {config.nSelection === "4" ? <>{renderParam("Used indicator", getIndicatorNameById(config.nInosUsedForValueInjection))}</> : null}

      {config.nSelection === "5" ? <>{renderParam("Used chart analyzer", getChartAnalyzerNameById(config.nCaSupResUsedForValueInjection))}</> : null}

      {config.nSelection !== "0" ? (
        <>
          <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />

          {config.bFixPipsMin === true
            ? renderParam("Minimum value in fix pips", getDisplayValue("nFixPipsMin"))
            : renderParam("Minimum value in fix pips", "Off")}

          {config.bRateOfChangeMin === true
            ? renderParam("Minimum value in rate of change", getDisplayValue("dRateOfChangeMin"))
            : renderParam("Minimum value in rate of change", "Off")}

          <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />

          {config.bFixPipsMax === true
            ? renderParam("Maximum value in fix pips", getDisplayValue("nFixPipsMax"))
            : renderParam("Maximum value in fix pips", "Off")}

          {config.bRateOfChangeMax === true
            ? renderParam("Maximum value in rate of change", getDisplayValue("dRateOfChangeMax"))
            : renderParam("Maximum value in rate of change", "Off")}
        </>
      ) : null}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const InitialTakeProfitConfigure = forwardRef(({ indicators, chartAnalyzers, onSave, initialData = null, professionalMode = null }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  InitialTakeProfitConfigure.propTypes = {
    indicators: PropTypes.any,
    chartAnalyzers: PropTypes.any,
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const autoCloseTimeoutRef = useRef(null);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});
    // Clear cross-validation error state
    setError(false);
    setReturnMessage("");
    
    const defaults = {
      ...InitialTakeProfitDefaults,
      nSelection: values.nSelection, // Keep the current selection
    };

    // Apply all the default values to the form
    Object.keys(defaults).forEach((key) => {
      setFieldValue(key, defaults[key]);
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      nSelection: initialData?.nSelection ?? InitialTakeProfitDefaults.nSelection,
      dRiskRatio: initialData?.dRiskRatio ?? InitialTakeProfitDefaults.dRiskRatio,
      nFixPipsActual: initialData?.nFixPipsActual ?? InitialTakeProfitDefaults.nFixPipsActual,
      dRateOfChangeActual: initialData?.dRateOfChangeActual ?? InitialTakeProfitDefaults.dRateOfChangeActual,
      // Updated logic for indicator selection
      nInosUsedForValueInjection: (() => {
        if (initialData?.nInosUsedForValueInjection) {
          return initialData.nInosUsedForValueInjection;
        }
        // Find first indicator with Signal used for = Forward (2)
        const forwardIndicators = indicators?.filter((ind) => ind.config?.nSignalUsedFor === "2") || [];
        return forwardIndicators.length > 0 ? forwardIndicators[0].id.toString() : InitialTakeProfitDefaults.nInosUsedForValueInjection;
      })(),
      // Updated logic for chart analyzer selection
      nCaSupResUsedForValueInjection: (() => {
        if (initialData?.nCaSupResUsedForValueInjection) {
          return initialData.nCaSupResUsedForValueInjection;
        }
        // Find first chart analyzer with Signal used for = Forward (2)
        const forwardChartAnalyzers = chartAnalyzers?.filter((ca) => ca.config?.nSignalUsedFor === "2") || [];
        return forwardChartAnalyzers.length > 0 ? forwardChartAnalyzers[0].id.toString() : InitialTakeProfitDefaults.nCaSupResUsedForValueInjection;
      })(),
      bFixPipsMin: initialData?.bFixPipsMin ?? InitialTakeProfitDefaults.bFixPipsMin,
      nFixPipsMin: initialData?.nFixPipsMin ?? InitialTakeProfitDefaults.nFixPipsMin,
      bRateOfChangeMin: initialData?.bRateOfChangeMin ?? InitialTakeProfitDefaults.bRateOfChangeMin,
      dRateOfChangeMin: initialData?.dRateOfChangeMin ?? InitialTakeProfitDefaults.dRateOfChangeMin,
      bFixPipsMax: initialData?.bFixPipsMax ?? InitialTakeProfitDefaults.bFixPipsMax,
      nFixPipsMax: initialData?.nFixPipsMax ?? InitialTakeProfitDefaults.nFixPipsMax,
      bRateOfChangeMax: initialData?.bRateOfChangeMax ?? InitialTakeProfitDefaults.bRateOfChangeMax,
      dRateOfChangeMax: initialData?.dRateOfChangeMax ?? InitialTakeProfitDefaults.dRateOfChangeMax,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          onSave(values);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
          console.error("Save error:", err);
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } = formik;

  // ----------------------------------------------------------------------
  // Cross-validation logic
  // ----------------------------------------------------------------------
  useEffect(() => {
    const nFixPipsMin = parseFloat(values.nFixPipsMin);
    const nFixPipsMax = parseFloat(values.nFixPipsMax);
    const dRateOfChangeMin = parseFloat(values.dRateOfChangeMin);
    const dRateOfChangeMax = parseFloat(values.dRateOfChangeMax);

    // Check Fix Pips Min vs Max
    if (values.bFixPipsMin === true && values.bFixPipsMax === true && 
        !Number.isNaN(nFixPipsMin) && !Number.isNaN(nFixPipsMax)) {
      if (nFixPipsMin >= nFixPipsMax) {
        setError(true);
        setReturnMessage(<><b>Minimum value in fix pips</b> cannot be greater or equal to <b>Maximum value in fix pips</b>!</>);
        return;
      }
    }

    // Check Rate of Change Min vs Max
    if (values.bRateOfChangeMin === true && values.bRateOfChangeMax === true && 
        !Number.isNaN(dRateOfChangeMin) && !Number.isNaN(dRateOfChangeMax)) {
      if (dRateOfChangeMin >= dRateOfChangeMax) {
        setError(true);
        setReturnMessage(<><b>Minimum value in rate of change</b> cannot be greater or equal to <b>Maximum value in rate of change</b>!</>);
        return;
      }
    }

    // If all validations pass, clear error state
    setError(false);
    setReturnMessage("");
  }, [values.bFixPipsMin, values.nFixPipsMin, values.bFixPipsMax, values.nFixPipsMax,
      values.bRateOfChangeMin, values.dRateOfChangeMin, values.bRateOfChangeMax, values.dRateOfChangeMax]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;
  if (values.nSelection === "4" && indicators && indicators.length === 0) {
    saveAndCloseDisabled = true;
  }
  if (
    values.nSelection === "4" &&
    (values.nInosUsedForValueInjection === "" || values.nInosUsedForValueInjection === "0" || values.nInosUsedForValueInjection === 0)
  ) {
    saveAndCloseDisabled = true;
  }
  if (values.nSelection === "5" && chartAnalyzers && chartAnalyzers.length === 0) {
    saveAndCloseDisabled = true;
  }
  if (
    values.nSelection === "5" &&
    (values.nCaSupResUsedForValueInjection === "" || values.nCaSupResUsedForValueInjection === "0" || values.nCaSupResUsedForValueInjection === 0)
  ) {
    saveAndCloseDisabled = true;
  }

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors()) {
    saveAndCloseDisabled = true;
  }

  // Disable save button if cross-validation fails
  if (error) {
    saveAndCloseDisabled = true;
  }

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure initial take profit
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure initial take profit</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nSelection */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter value="Selection" helpReference="HandleInitialTakeProfit" helpText="" helpLink="" fieldName="nSelection" touched={touched} errors={errors} />
                    <FormControl fullWidth>
                      <Select
                        {...getFieldProps("nSelection")}
                        input={<ModalContentOutlinedInput />}
                        error={Boolean(touched.nSelection && errors.nSelection)}
                      >
                        <ModalContentMenuItem value="0">Disabled</ModalContentMenuItem>
                        <ModalContentMenuItem value="1">Risk ratio</ModalContentMenuItem>
                        <ModalContentMenuItem value="2">Fix pips</ModalContentMenuItem>
                        <ModalContentMenuItem value="3">Rate of change</ModalContentMenuItem>
                        {professionalMode === true || values.nSelection === "4" ? (
                          <ModalContentMenuItem value="4">Indicator value</ModalContentMenuItem>
                        ) : null}
                        {professionalMode === true || values.nSelection === "5" ? (
                          <ModalContentMenuItem value="5">Support/resistance from chart analyzer</ModalContentMenuItem>
                        ) : null}
                        {professionalMode === true || values.nSelection === "6" ? (
                          <ModalContentMenuItem value="6">Candle patterns</ModalContentMenuItem>
                        ) : null}
                        {professionalMode === true || values.nSelection === "7" ? (
                          <ModalContentMenuItem value="7">Chart patterns</ModalContentMenuItem>
                        ) : null}
                        {professionalMode === true || values.nSelection === "8" ? (
                          <ModalContentMenuItem value="8">Fibonacci patterns</ModalContentMenuItem>
                        ) : null}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* dRiskRatio */}
                  {/* ---------------------------------------------------------------------- */}
                  {values.nSelection === "1" ? (
                    <Box>
                      <SmartParameter value="Risk ratio" helpReference="HandleInitialTakeProfit" helpText="" helpLink="" fieldName="dRiskRatio" touched={touched} errors={errors} />
                      <SmartNumberInput
                        textFieldName="dRiskRatio"
                        defaultLimits={DEFAULT_LIMITS}
                        formik={formik}
                      />
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nFixPipsActual */}
                  {/* ---------------------------------------------------------------------- */}
                  {values.nSelection === "2" ? (
                    <Box>
                      <SmartParameter value="Fix pips" helpReference="HandleInitialTakeProfit" helpText="" helpLink="" fieldName="nFixPipsActual" touched={touched} errors={errors} />
                      <SmartNumberInput
                        textFieldName="nFixPipsActual"
                        defaultLimits={DEFAULT_LIMITS}
                        formik={formik}
                      />
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* dRateOfChangeActual */}
                  {/* ---------------------------------------------------------------------- */}
                  {values.nSelection === "3" ? (
                    <Box>
                      <SmartParameter value="Rate change" helpReference="HandleInitialTakeProfit" helpText="" helpLink="" fieldName="dRateOfChangeActual" touched={touched} errors={errors} />
                      <SmartNumberInput
                        textFieldName="dRateOfChangeActual"
                        defaultLimits={DEFAULT_LIMITS}
                        formik={formik}
                      />
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nInosUsedForValueInjection */}
                  {/* ---------------------------------------------------------------------- */}
                  {values.nSelection === "4" &&
                  (() => {
                    const forwardIndicators = indicators?.filter((ind) => ind.config?.nSignalUsedFor === "2") || [];
                    return forwardIndicators.length > 0;
                  })() ? (
                    <Box>
                      <SmartParameter
                        value="Used indicator"
                        helpReference="HandleInitialTakeProfit"
                        helpText=""
                        helpLink=""
                        fieldName="nInosUsedForValueInjection"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nInosUsedForValueInjection")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nInosUsedForValueInjection && errors.nInosUsedForValueInjection)}
                        >
                          <ModalContentMenuItem value="0">
                            <Box>
                              <Typography variant="contentSmall">Please select ...</Typography>
                            </Box>
                          </ModalContentMenuItem>
                          {indicators
                            ?.filter((indicator) => indicator.config?.nSignalUsedFor === "2")
                            .map((indicator) => (
                              <ModalContentMenuItem key={indicator.id.toString()} value={indicator.id.toString()}>
                                <Box>
                                  <Typography variant="contentSmall">
                                    {indicator.name} ({indicator.subtitle})
                                  </Typography>
                                </Box>
                              </ModalContentMenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ) : null}

                  {values.nSelection === "4" &&
                  (() => {
                    const forwardIndicators = indicators?.filter((ind) => ind.config?.nSignalUsedFor === "2") || [];
                    return forwardIndicators.length === 0;
                  })() ? (
                    <Box sx={{ m: 0, p: 0 }}>
                      <ModalAlertError
                        sx={{
                          m: "0 !important",
                        }}
                        severity="error"
                      >
                        No indicator with <b>Signal used for = Forward</b> is available. Please add at least one indicator and set its{" "}
                        <b>Signal used for</b> setting to <b>Forward</b>.
                      </ModalAlertError>
                    </Box>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nCaSupResUsedForValueInjection */}
                  {/* ---------------------------------------------------------------------- */}
                  {values.nSelection === "5" &&
                  (() => {
                    const forwardChartAnalyzers = chartAnalyzers?.filter((ca) => ca.config?.nSignalUsedFor === "2") || [];
                    return forwardChartAnalyzers.length > 0;
                  })() ? (
                    <Box>
                      <SmartParameter
                        value="Used chart analyzer"
                        helpReference="HandleInitialTakeProfit"
                        helpText=""
                        helpLink=""
                        fieldName="nCaSupResUsedForValueInjection"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nCaSupResUsedForValueInjection")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nCaSupResUsedForValueInjection && errors.nCaSupResUsedForValueInjection)}
                        >
                          <ModalContentMenuItem value="0">
                            <Box>
                              <Typography variant="contentSmall">Please select ...</Typography>
                            </Box>
                          </ModalContentMenuItem>
                          {chartAnalyzers
                            ?.filter((chartAnalyzer) => chartAnalyzer.config?.nSignalUsedFor === "2")
                            .map((chartAnalyzer) => (
                              <ModalContentMenuItem key={chartAnalyzer.id} value={chartAnalyzer.id.toString()}>
                                <Box>
                                  <Typography variant="contentSmall">
                                    {chartAnalyzer.name} ({chartAnalyzer.subtitle})
                                  </Typography>
                                </Box>
                              </ModalContentMenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ) : null}

                  {values.nSelection === "5" &&
                  (() => {
                    const forwardChartAnalyzers = chartAnalyzers?.filter((ca) => ca.config?.nSignalUsedFor === "2") || [];
                    return forwardChartAnalyzers.length === 0;
                  })() ? (
                    <Box sx={{ m: 0, p: 0 }}>
                      <ModalAlertError
                        sx={{
                          m: "0 !important",
                        }}
                        severity="error"
                      >
                        No chart analyzer with <b>Signal used for = Forward</b> is available. Please add at least one chart analyzer and set its{" "}
                        <b>Signal used for</b> setting to <b>Forward</b>.
                      </ModalAlertError>
                    </Box>
                  ) : null}

                  {values.nSelection !== "0" ? (
                    <>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 2,
                        }}
                      >
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nFixPipsMin */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Minimum value in fix pips"
                            helpReference="HandleInitialTakeProfit"
                            helpText=""
                            helpLink=""
                            fieldName="nFixPipsMin"
                            touched={touched}
                            errors={errors}
                          />
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <ModalContentSwitch
                              sx={{ my: "12px" }}
                              checked={values.bFixPipsMin === true}
                              onChange={(event) => {
                                setFieldValue("bFixPipsMin", event.target.checked);
                              }}
                            />
                            {values.bFixPipsMin === true ? (
                              <SmartNumberInput
                                textFieldName="nFixPipsMin"
                                defaultLimits={DEFAULT_LIMITS}
                                formik={formik}
                              />
                            ) : null}
                          </Box>
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dRateOfChangeMin */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Minimum value in rate of change"
                            helpReference="HandleInitialTakeProfit"
                            helpText=""
                            helpLink=""
                            fieldName="dRateOfChangeMin"
                            touched={touched}
                            errors={errors}
                          />
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <ModalContentSwitch
                              sx={{ my: "12px" }}
                              checked={values.bRateOfChangeMin === true}
                              onChange={(event) => {
                                setFieldValue("bRateOfChangeMin", event.target.checked);
                              }}
                            />
                            {values.bRateOfChangeMin === true ? (
                              <SmartNumberInput
                                textFieldName="dRateOfChangeMin"
                                defaultLimits={DEFAULT_LIMITS}
                                formik={formik}
                              />
                            ) : null}
                          </Box>
                        </Box>
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 2,
                        }}
                      >
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nFixPipsMax */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Maximum value in fix pips"
                            helpReference="HandleInitialTakeProfit"
                            helpText=""
                            helpLink=""
                            fieldName="nFixPipsMax"
                            touched={touched}
                            errors={errors}
                          />
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <ModalContentSwitch
                              sx={{ my: "12px" }}
                              checked={values.bFixPipsMax === true}
                              onChange={(event) => {
                                setFieldValue("bFixPipsMax", event.target.checked);
                              }}
                            />
                            {values.bFixPipsMax === true ? (
                              <SmartNumberInput
                                textFieldName="nFixPipsMax"
                                defaultLimits={DEFAULT_LIMITS}
                                formik={formik}
                              />
                            ) : null}
                          </Box>
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dRateOfChangeMax */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter
                            value="Maximum value in rate of change"
                            helpReference="HandleInitialTakeProfit"
                            helpText=""
                            helpLink=""
                            fieldName="dRateOfChangeMax"
                            touched={touched}
                            errors={errors}
                          />
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <ModalContentSwitch
                              sx={{ my: "12px" }}
                              checked={values.bRateOfChangeMax === true}
                              onChange={(event) => {
                                setFieldValue("bRateOfChangeMax", event.target.checked);
                              }}
                            />
                            {values.bRateOfChangeMax === true ? (
                              <SmartNumberInput
                                textFieldName="dRateOfChangeMax"
                                defaultLimits={DEFAULT_LIMITS}
                                formik={formik}
                              />
                            ) : null}
                          </Box>
                        </Box>
                      </Box>
                    </>
                  ) : null}
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
